import React, { Fragment, useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import useRequests from "../../../utils/useRequests";
import * as Yup from "yup";
import { useFormik } from "formik";
import Snackbars from "../../../utils/Snackbars";
import { Spinner } from "react-bootstrap";

const EditVenue = () => {
    const { v_id } = useParams();
    const navigate = useNavigate();
    const { getRequest, postRequest } = useRequests();
    const [loading, setLoading] = useState(true);
    
    const [formData, setFormData] = useState({
        name: "",
        address: "",
        city: "",
        capacity: "",
        description: "",
        status: "Enable",
    });

    const [AlertData, setAlertData] = React.useState({
        variant: '',
        message: '',
    });

    const resetAlertData = () => {
        setTimeout(() => {
            setAlertData((AlertData) => ({
                ...AlertData,
                variant: '',
                message: '',
            }));
        }, process.env.REACT_APP_ALERT_AUTO_CLOSE);
    };

    const fetchVenueDetails = async () => {
        try {
            const response = await getRequest(`venue-details/${v_id}`);
            if (response && response.code === 200) {
                const data = response.data;
                setFormData({
                    name: data.name || "",
                    address: data.address || "",
                    city: data.city || "",
                    capacity: data.capacity || "",
                    description: data.description || "",
                    status: data.status || "Enable",
                });
            } else {
                setAlertData({ 
                    variant: "error", 
                    message: response?.message || "Failed to fetch venue details" 
                });
                resetAlertData();
            }
        } catch (error) {
            console.error("Error fetching venue details:", error);
            setAlertData({ 
                variant: "error", 
                message: "An error occurred while fetching venue details" 
            });
            resetAlertData();
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            await fetchVenueDetails();
            setLoading(false);
        };
        fetchData();
    }, [v_id]);

    const validation1 = useFormik({
        enableReinitialize: true,
        initialValues: formData,
        validationSchema: Yup.object({
            name: Yup.string().required('Venue Name is required'),
            address: Yup.string().required('Address is required'),
            city: Yup.string().required('City is required'),
            capacity: Yup.number()
                .required('Capacity is required')
                .min(1, 'Capacity must be at least 1')
                .max(999999, 'Capacity is too large'),
            description: Yup.string().required('Description is required'),
            status: Yup.string()
                .required('Status is required')
                .oneOf(['Enable', 'Disable'], 'Invalid status selection'),
        }),
        onSubmit: async (values) => {
            try {
                let submit1 = document.getElementById('submit1');
                submit1.disabled = true;
                submit1.textContent = ('Processing...');

                const jsonpar = JSON.stringify(values);
                const res = await postRequest(`venue-edit/${v_id}`, jsonpar);

                if (res && res.code === 200) {
                    navigate('/venue-list', {
                        state: { variant: "success", message: "Venue updated successfully!" }
                    });
                } else {
                    setAlertData((AlertData) => ({
                        ...AlertData,
                        variant: 'error',
                        message: res?.message || "Failed to update venue.",
                    }));
                    resetAlertData();
                }

                submit1.disabled = false;
                submit1.textContent = ('Update Venue');
            } catch (error) {
                console.error("Error: ", error);
                setAlertData((AlertData) => ({
                    ...AlertData,
                    variant: 'error',
                    message: "An error occurred while updating the venue.",
                }));
                resetAlertData();
                
                let submit1 = document.getElementById('submit1');
                if (submit1) {
                    submit1.disabled = false;
                    submit1.textContent = ('Update Venue');
                }
            }
        }
    });

    if (loading) {
        return (
            <div className="text-center" style={{ marginTop: "100px" }}>
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Loading venue details...</p>
            </div>
        );
    }

    return (
        <Fragment>
            <div className="d-sm-flex d-block justify-content-between align-items-center mb-4">
                <h2 className="text-black font-w600 mb-0">
                    <i className="flaticon-381-location me-2"></i>
                    Edit Venue
                </h2>
                <Link to="/venue-list" className="btn btn-rounded btn-primary">
                    <i className="fa-solid fa-arrow-left me-2"></i>
                    Back to List
                </Link>
            </div>

            <div className="col-lg-12">
                <div className="card">
                    <div className="card-body">
                        <div className="basic-form">
                            {AlertData.message !== "" ? (
                                <Snackbars
                                    variant={AlertData.variant}
                                    message={AlertData.message}
                                />
                            ) : (
                                ""
                            )}
                            <form onSubmit={(e) => e.preventDefault()}>
                                <div className="row mb-3">
                                    <div className="form-group mb-3 col-md-6 col-xs-12">
                                        <label>Venue Name <span className="text-danger">*</span>:</label>
                                        <input 
                                            name="name" 
                                            type="text"
                                            className={`form-control ${validation1.touched.name && validation1.errors.name ? 'is-invalid' : ''}`}
                                            placeholder="Please Enter Venue Name"
                                            value={validation1.values.name || ''}
                                            onChange={validation1.handleChange}
                                            onBlur={validation1.handleBlur}
                                        />
                                        {validation1.errors.name && validation1.touched.name && (
                                            <small className="text-danger">{validation1.errors.name}</small>
                                        )}
                                    </div>
                                    <div className="form-group mb-3 col-md-6 col-xs-12">
                                        <label>City <span className="text-danger">*</span>:</label>
                                        <input 
                                            type="text"
                                            placeholder="Please Enter City"
                                            name="city"
                                            className={`form-control ${validation1.touched.city && validation1.errors.city ? 'is-invalid' : ''}`}
                                            value={validation1.values.city || ''}
                                            onChange={validation1.handleChange}
                                            onBlur={validation1.handleBlur}
                                        />
                                        {validation1.errors.city && validation1.touched.city && (
                                            <small className="text-danger">{validation1.errors.city}</small>
                                        )}
                                    </div>
                                    <div className="form-group mb-3 col-md-8 col-xs-12">
                                        <label>Address <span className="text-danger">*</span>:</label>
                                        <input 
                                            type="text"
                                            placeholder="Please Enter Address" 
                                            name="address"
                                            className={`form-control ${validation1.touched.address && validation1.errors.address ? 'is-invalid' : ''}`}
                                            value={validation1.values.address || ''}
                                            onChange={validation1.handleChange}
                                            onBlur={validation1.handleBlur}
                                        />
                                        {validation1.errors.address && validation1.touched.address && (
                                            <small className="text-danger">{validation1.errors.address}</small>
                                        )}
                                    </div>
                                    <div className="form-group mb-3 col-md-4">
                                        <label>Capacity <span className="text-danger">*</span>:</label>
                                        <input
                                            type="number"
                                            placeholder="Please Enter Capacity" 
                                            name="capacity"
                                            className={`form-control ${validation1.touched.capacity && validation1.errors.capacity ? 'is-invalid' : ''}`}
                                            value={validation1.values.capacity || ''}
                                            onChange={validation1.handleChange}
                                            onBlur={validation1.handleBlur}
                                            min="1"
                                            max="999999"
                                        />
                                        {validation1.errors.capacity && validation1.touched.capacity && (
                                            <small className="text-danger">{validation1.errors.capacity}</small>
                                        )}
                                    </div>
                                    <div className="form-group mb-3 col-md-12">
                                        <label>Description <span className="text-danger">*</span>:</label>
                                        <textarea 
                                            rows={4}
                                            placeholder="Please Enter Venue Description" 
                                            name="description"
                                            className={`form-control ${validation1.touched.description && validation1.errors.description ? 'is-invalid' : ''}`}
                                            value={validation1.values.description || ''}
                                            onChange={validation1.handleChange}
                                            onBlur={validation1.handleBlur}
                                        />
                                        {validation1.errors.description && validation1.touched.description && (
                                            <small className="text-danger">{validation1.errors.description}</small>
                                        )}
                                    </div>
                                    <div className="form-group mb-3 col-md-4">
                                        <label>Status <span className="text-danger">*</span>:</label>
                                        <select 
                                            className={`form-control ${validation1.touched.status && validation1.errors.status ? 'is-invalid' : ''}`} 
                                            value={validation1.values.status || 'Enable'}
                                            name="status"
                                            onChange={validation1.handleChange}
                                            onBlur={validation1.handleBlur}
                                        >
                                            <option value="Enable">Enable</option>
                                            <option value="Disable">Disable</option>
                                        </select>
                                        {validation1.errors.status && validation1.touched.status && (
                                            <small className="text-danger">{validation1.errors.status}</small>
                                        )}
                                    </div>
                                </div>
                                <button
                                    type="submit" 
                                    className="btn btn-primary me-2"    
                                    id="submit1"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        validation1.handleSubmit();
                                        return false;
                                    }}>
                                    Update Venue
                                </button>
                                <Link to="/venue-list" className="btn btn-light">
                                    Cancel
                                </Link>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default EditVenue;